/* THE AI-FILLED MARKER MAP — Phase 6.5 step 4.1, RE-PURPOSED 4.3.
 *
 * ── ⚠⚠⚠ THE NAME IS HISTORY AND THE CONCEPT HAS CHANGED — 2026-09-21 ─
 * This file is still `pending.js` **on his instruction**, because the map
 * mechanics are identical and renaming it would churn four gates, the
 * twin's four subjects and `anchor-sweep` for no behavioural gain.
 * **What changed is what the map MEANS.**
 *
 *   BEFORE (his ruling 4, 2026-09-15)   a PENDING SUGGESTION: a value the
 *     collector had not accepted, held OFF the holding so it could not
 *     become a fact, confirmed per field by a tap.
 *   NOW (his ruling, 2026-09-21)        a MARKER: the AI has ALREADY
 *     filled the field, the value IS on the holding, and this map only
 *     records THAT IT WAS THE AI so a ✨ can say so.
 *
 * ⚠⚠ SO `setPending` MARKS; IT DOES NOT HOLD A VALUE THE APP IS WAITING
 * ON. The entry still carries `value`, because the marker is per FIELD
 * and knowing what was filled is what lets a later edit tell "still the
 * AI's" from "the collector changed it".
 *
 * ⚠⚠⚠ AND THE SAFETY PROPERTY IS NOT WHAT IT WAS. It used to be *a
 * suggestion must not be able to become a fact by accident* — and his
 * 2026-09-21 ruling deliberately withdraws that: autosave writes the
 * value the moment it lands, so it reaches `save()`, `holdingToRow()`
 * and `snapshot()` BY DESIGN. **What survives, and what this map must
 * still guarantee, is the narrower property: the MARKER reaches none of
 * the three.** A marker that leaked would put an app-internal flag on a
 * stranger's public profile and into Postgres.
 *
 * ── ⚠ MEMORY-ONLY — STILL TRUE, AND STILL HIS RULING ────────────────
 * *"Memory-only. Nothing new is stored."* `state` is rebuilt on load, so
 * the map is empty on every boot BY CONSTRUCTION. ⚠⚠ The consequence he
 * accepted: **after a reload an AI-filled value is indistinguishable
 * from a typed one.** His reason is that a marker is honest either way
 * and a confirm step that is often wrong is not.
 *
 * ⚠ The map lives at `state.pending`, keyed `holdingId:field`, and NEVER
 * on a holding — which is what keeps the marker out of all three paths
 * by SHAPE rather than by a filter three places could forget.
 */

/**
 * ⚠⚠⚠ TWO FIELDS, NOT SEVEN — HIS RULINGS, 2026-09-20 (option 4) AND
 * 2026-09-21 (option 3 on `service`). THE COUNT IS THE RULING.
 *
 * A reading returns NINE (`FIELD_NAMES` in `engine-vision.mjs`) since
 * step 7.1 added the two star signals. SEVEN
 * of them are never suggested, and each is excluded for a stated reason
 * rather than by taste — see `NOT_SUGGESTED` below.
 *
 * ⚠⚠ USING A READING TO IDENTIFY AN UNKNOWN NOTE IS THE SLAB LOOKUP,
 * WHICH IS **STEP 7** AND NOT THIS STEP. A note reaching this model has
 * a settled `catId`, so `denomination`, `series` and `fr_number` have
 * nothing to do here.
 *
 * ⚠⚠⚠ AND THIS IS WHY HIS RULING 4 IS AFFORDABLE. He ruled per-field
 * confirmation and then priced it at seven taps a note, which he called
 * a cost he had not priced properly. **The real number is two** — and
 * one of them is `grade`, the field where a wrong value misprices the
 * note, which is exactly where he wants a deliberate act. The ruling did
 * not need changing; the count did, twice.
 */
export const PENDING_FIELDS = Object.freeze([
  'grade',
  'qualifier',
])

/* ── STEP 7.3(c), 2026-09-23: THE IDENTITY MARKER ────────────────────
 * The slab lookup changes ONE field on the holding — `catId` — and the
 * ✨ that says the app did it is a marker on that field. It is NOT a
 * value field: no reading delivers a `catId`, so it is not in
 * `PENDING_FIELDS` (the count there is the ruling and the accounting
 * against the encoder's list closes without it). It is a marker the
 * map may carry, and `MARKED_FIELDS` is the whole list of those —
 * **two value fields and one identity marker**, the line `pending-check`
 * §1 asserts with this reason at it. */
export const IDENTITY_FIELD = 'catId'
/* ── STEP 6, HIS RULING (2), 2026-09-26: THE RAW FILL MARKER ──────────
 * A RAW reading (the v3 prompt, no label) fills ONE field on the note
 * side — the custom entry's Denomination, when it is EMPTY and no
 * candidate row has been tapped — and marks it with the ✨. *"never
 * Type"*: that field is a select of catalogue sections and the reading
 * is a printed heading, so it is not filled. Not in `PENDING_FIELDS`
 * (that list is the LABEL prompt's accounting, and this is a different
 * prompt's field); a marker key like the identity's. */
export const RAW_FILL_FIELD = 'denom'
/* ── HIS RULING 2 OF 2026-10-26: THE YEAR MARKER — *"Fill the year from the
 * photo's series when the field is empty, with ✨, on every custom note."*
 * The holding's own year, filled from either prompt's `series` (label or
 * raw); a marker key like the denomination's, not in `PENDING_FIELDS`. */
export const YEAR_FILL_FIELD = 'year'
export const MARKED_FIELDS = Object.freeze([...PENDING_FIELDS, IDENTITY_FIELD, RAW_FILL_FIELD, YEAR_FILL_FIELD])

/** The SEVEN a reading returns and this never suggests, with the reason
 *  each is out. ⚠ NAMED, not merely absent: a field that quietly stopped
 *  being suggested would look the same as one nobody had thought of. */
export const NOT_SUGGESTED = Object.freeze({
  denomination: 'the catalogue row already says it',
  series: 'the catalogue row already says it — and on a CUSTOM note it fills the year, a marker (YEAR_FILL_FIELD), not a suggestion (his ruling 2 of 2026-10-26)',
  /* ⚠ REWRITTEN 2026-09-23 WITH 7.3(c): the three identity fields are now
   * CONSUMED — `applyReading` hands them to `matchFr` and, on a confident
   * match, converts the note and marks `catId`. They are still never
   * SUGGESTED as values, which is what this list is about. */
  fr_number: 'consumed by 7.3 — it finds the row, and a confident match is marked on catId; it is never a value on a note',
  cert: 'his ruling 3 — the cert is typed, never machine-written',
  /* ⚠⚠⚠ HIS RULING, 2026-09-21 — option 3, and it is recorded as a
   * ruling with its reason rather than as the feature failing.
   * The reader returns `PCGS Currency`; the memo format takes ONE
   * all-caps token of 2–6 letters, so `withHolder('', 'PCGS Currency')`
   * writes NOTHING — measured. Suggesting the service therefore means
   * first ruling on a mapping (`PCGS Currency` → `PCGS`? and is
   * `PCGS Banknote` the same token?) that decides what the collector's
   * own memo says. **Not suggesting it avoids that decision entirely**,
   * and it takes the tap cost from three to two.
   * ⚠ It is the shape ruling 3 of 2026-09-15 already took for `cert`. */
  service: 'his ruling 2026-09-21 — typed like the cert; the reader returns "PCGS Currency" where the memo format takes one token',
  /* ⚠⚠ ADDED 2026-09-22 WITH STEP 7.1. These two are not fields on a
   * note at all — they are EVIDENCE ABOUT WHICH NOTE IT IS, consumed by
   * `src/slab-match.js` and never shown to a collector or written to a
   * holding. ⚠ They are named here rather than left out because the
   * accounting line in `pending-check` is what makes "the feature does
   * exactly what it says" checkable: a field absent from both lists
   * looks identical to one nobody thought about. */
  star_label: 'not a field on a note — evidence about WHICH note this is, consumed by 7.3 through matchFr',
  star_serial: 'not a field on a note — the second of his two independent star signals, consumed by 7.3 through matchFr',
})

/** ⚠ One key per holding AND field — his ruling 4 is per-field, so the
 *  key has to be too. A key per holding would make accept-all the only
 *  reachable shape. */
export const pendingKey = (holdingId, field) => `${holdingId}:${field}`

/**
 * Record a suggestion. ⚠⚠⚠ IT GOES ON `state`, NOT ON THE HOLDING —
 * that one choice is the safety property, and `pending-check` proves it
 * against all three paths rather than trusting this comment.
 */
export function setPending(state, holdingId, field, suggestion) {
  /* ⚠ `MARKED_FIELDS`, not `PENDING_FIELDS`, since 7.3(c): the identity
   * marker is the one key that is not a value field. */
  if (!MARKED_FIELDS.includes(field)) return null
  if (!state.pending) state.pending = {}
  const entry = {
    holdingId: String(holdingId),
    field,
    value: suggestion && suggestion.value,
    source: (suggestion && suggestion.source) || 'read',
    engine: (suggestion && suggestion.engine) || null,
    ms: (suggestion && suggestion.ms) ?? null,
    /* ⚠ 7.3(c), his detail 2026-09-23: the IDENTITY marker records what
     * the conversion itself filled (`{ year, grade }`, each the value
     * written or null), so "Not the right note?" can clear exactly those
     * — they belong to the wrong row — and never a typed value. Null on
     * the value markers. */
    filled: (suggestion && suggestion.filled) || null,
  }
  state.pending[pendingKey(holdingId, field)] = entry
  return entry
}

export const getPending = (state, holdingId, field) =>
  (state.pending || {})[pendingKey(holdingId, field)] || null

/** Every pending suggestion for one holding, in `PENDING_FIELDS` order.
 *  ⚠ The order is the field list's, not insertion order, so the editor
 *  shows them the same way every time. */
export const pendingFor = (state, holdingId) =>
  PENDING_FIELDS.map((f) => getPending(state, holdingId, f)).filter(Boolean)

/**
 * Drop one suggestion — what ACCEPT and REJECT both do here.
 * ⚠ Accepting writes the value to the holding somewhere else (4.3); this
 * module never writes a holding, so a bug in the confirm step cannot
 * turn into a silent write from here.
 */
export function clearPending(state, holdingId, field) {
  if (!state.pending) return false
  return delete state.pending[pendingKey(holdingId, field)]
}

/** Drop every marker for a holding — leaving its editor, or Save.
 *  ⚠ ALL THREE keys since 7.3(c), AND the lookup's refusal line: one
 *  call forgets everything the AI said about a note, so there is one
 *  place to forget rather than two that agree today. */
export function clearPendingFor(state, holdingId) {
  for (const f of MARKED_FIELDS) clearPending(state, holdingId, f)
  clearLookup(state, holdingId)
  clearDifferent(state, holdingId)
  clearReading(state, holdingId)
  clearUnchecked(state, holdingId) /* his ruling of 2026-10-22: memory-only, like the prompts */
}

export const hasPending = (state) => Object.keys(state.pending || {}).length > 0
export const pendingCount = (state) => Object.keys(state.pending || {}).length

/* ══ THE LOOKUP'S REFUSAL LINE — STEP 7.3(b), 2026-09-23 ═══════════════
 *
 * When the slab lookup REFUSES to identify a note, the add screen shows
 * ONE PLAIN LINE under the Fr field and never silence — his ruling 1 of
 * 2026-09-22 — and the line says WHICH refusal, because each names a
 * different thing to do. The four strings are his, ruled FINAL
 * 2026-09-23 (ruling 3), and they live here so there is one copy.
 *
 * ⚠ THE STATE IS A SIBLING OF THE MARKER MAP, NOT AN ENTRY IN IT. Every
 * marker entry renders a ✨ and a refusal must not. It is memory-only,
 * on `state.lookup` and NEVER on a holding — the same shape guarantee
 * as `state.pending`: `save()`, `holdingToRow()` and `snapshot()` cannot
 * reach what is not on the holding. Keyed by holding, one entry each,
 * because a later landing's outcome REPLACES the last (a match removes
 * the card entirely; a new refusal writes its own reason).
 *
 * ~~⚠⚠ (b) IS PIXELS ONLY — the handlers are not wired.~~ ✅ WIRED IN (c),
 * 2026-09-23: `applyReading` (suggest.js) writes it on a refusal and
 * clears it on a match; `clearPendingFor` clears it with the markers
 * (one place to forget, not two); a `#cfr` keystroke clears it
 * (main.js); a later front landing replaces it. ⚠ A BACK landing must
 * not clear a line the front put there, and a FAILED reading
 * (`read.ok !== true`) writes no line at all — that silence is the
 * state the line exists to be distinguishable from. `slab-check` §9
 * drives each of these.
 *
 * ⚠ `no-fr` SPEAKS ONLY WHEN A GRADING SERVICE WAS READ — his ruling 3:
 * a raw note added with the button carries no Fr at all, so on a raw
 * note it stays silent. That condition is the WRITER's ((c)), not this
 * table's: the string exists for the slab whose Fr could not be read.
 */
/* ⚠⚠ REWORDED ON HIS INSTRUCTION FROM THE (b) RENDERS, 2026-09-23: the
 * first strings said *"pick it from the catalog"*, and there is no button
 * for that — going to Resources ADDS a second note while the autosaved
 * custom one keeps the photo. Every line now points at the Fr field
 * directly above, which already converts THIS note. A Pick-from-catalog
 * button that REPLACES the note instead of adding one is a register
 * item, not this step. ⚠ `no-fr` said "catalog" too and is reworded on
 * the same reasoning. */
export const LOOKUP_LINES = Object.freeze({
  'star-unsure': () => "Couldn't tell if this is a star note — enter the Fr. number above to link it.",
  'no-row': (fr) => 'Read Fr. ' + fr + " on the label but it isn't in the catalog — enter the Fr. number above to link it.",
  /* ⚠ AMBIGUOUS CANNOT POINT AT THE Fr FIELD — his catch, 2026-09-23:
   * typing the same Fr also refuses on more than one match (the typed
   * path's `m.length===1`), so that line would be a dead end. It says
   * what is true. Ten Fr values are in this position, and it is the case
   * the later Pick-from-catalog button (register) would solve. */
  'ambiguous': (fr) => 'Fr. ' + fr + ' matches more than one catalog note — this one has to be added from the catalog.',
  'no-fr': () => 'No Fr. number was read from this photo — enter the Fr. number above to link it.',
  /* ── THE SECOND AGREEMENT, HIS RULING 2026-09-23 — each disagreement its
   * own line, and the line SHOWS BOTH VALUES so a misread digit is on the
   * screen rather than silent: the 7.4 run read `1952-D` for his `2055-B`
   * and the catalogue's 1952-D is a $5 note; the label said $20. ⚠ An
   * ABSENT reading is the same reason with `read` null, and the line says
   * so instead of printing an empty value. ✅ BOTH STRINGS ARE FINAL AS
   * WRITTEN — his ruling, 2026-09-24. */
  'denomination-mismatch': (fr, x) => (x && x.read
    ? 'Read Fr. ' + fr + ' on the label, but the label says ' + x.read + ' and that catalog note is ' + (x.want || 'something else') + ' — enter the Fr. number above to link it.'
    : 'Read Fr. ' + fr + ' on the label but no denomination to check it against — enter the Fr. number above to link it.'),
  'series-mismatch': (fr, x) => 'Read Fr. ' + fr + ' on the label, but the label says Series ' + (x && x.read || '?') + ' and that catalog note is ' + (x && x.want || '?') + ' — enter the Fr. number above to link it.',
})

/** The line for one refusal, as PLAIN TEXT — the view escapes it, because
 *  `fr` is what the reader returned and a reading is untrusted bytes.
 *  `extra` is the lookup entry itself (`read` / `want` on a mismatch).
 *  ⚠ An unknown reason returns null rather than a generic sentence: a
 *  refusal this table cannot name is a gap to be seen, not papered. */
export function lookupLine(reason, fr, extra = null) {
  const f = LOOKUP_LINES[reason]
  return f ? f(fr == null ? '' : String(fr), extra) : null
}

export const lookupKey = (holdingId) => String(holdingId)

/** Record a refusal for one holding. ⚠ ON `state`, NEVER ON THE HOLDING. */
export function setLookup(state, holdingId, refusal) {
  if (!refusal || !LOOKUP_LINES[refusal.reason]) return null
  if (!state.lookup) state.lookup = {}
  const entry = {
    holdingId: String(holdingId), reason: refusal.reason, fr: refusal.fr == null ? null : String(refusal.fr),
    /* a mismatch's two values, so the line can show both; null elsewhere */
    read: refusal.read == null ? null : String(refusal.read),
    want: refusal.want == null ? null : String(refusal.want),
  }
  state.lookup[lookupKey(holdingId)] = entry
  return entry
}

export const getLookup = (state, holdingId) =>
  (state.lookup || {})[lookupKey(holdingId)] || null

export function clearLookup(state, holdingId) {
  if (!state.lookup) return false
  return delete state.lookup[lookupKey(holdingId)]
}

/* ══ STEP 7.5, HIS RULING 2026-09-25 — THE DIFFERENT-NOTE PROMPT (B) ══
 *
 * A front photo on a CATALOGUE note whose reading matches a DIFFERENT row
 * with confidence. His walk: he replaced a note's photo with another
 * note's, and the reading was made and discarded without a word. Now the
 * note asks, and only the collector's tap changes anything, because the
 * tap is the consent (his words, for the year and the grade alike).
 * ⚠ ON `state`, NEVER ON THE HOLDING, like the lookup line: memory-only,
 * gone on Save, on leaving and on reload. ⚠ The wording is HIS, approved
 * 2026-09-25, and is final as written. `row` is the catalogue's own row,
 * but `fr` and `series` are still escaped by the view. */
/* ⚠ HIS RULING 1 OF 2026-10-06: *"fix the slab prompt's "This note is Fr. …"
 * so it never prints "Fr. " with nothing after it: name the note the way its
 * vault card titles it."* 850 Obsolete rows carry no Fr (measured that day);
 * on those `title` — the view passes `cardTitle` — names the note
 * (*"Michigan · $3"*). A note with an Fr reads exactly as approved. */
export const DIFFERENT_LINE = (row, currentFr, title = '') => {
  const rest = [row.series ? 'Series ' + row.series : '', row.denom || ''].filter(Boolean).join(' ')
  return 'This photo looks like a different note: Fr. ' + row.fr + (rest ? ', ' + rest : '') + '. This note is ' + (String(currentFr == null ? '' : currentFr).trim() ? 'Fr. ' + currentFr : title) + '.'
}
export const DIFFERENT_UPDATE = 'Update this note'
export const DIFFERENT_KEEP = 'Keep as is'

/* ══ HIS RULINGS OF 2026-10-06 — A CATALOGUE NOTE'S RAW FRONT PHOTO ══════
 *
 * His walk: on a note chosen from Resources he photographed a DIFFERENT,
 * raw note, and nothing showed — the reading was made, charged and dropped,
 * because 7.5's prompt needs an Fr and a raw reading has none. His rulings:
 * the prompt on a catalogue note's raw front photo, DENOMINATION ONLY (the
 * saved v3 run: the denomination right on 19 of his 19 fronts, the type on
 * 16), and *"Don't name the note by Fr. The line reads "This photo looks
 * like a $5 note, but this note is a $1." It works for every type; the
 * header already names the note."* Buttons: *"See likely notes"* opens the
 * whole reading card on this note (his ruling 3); *"Keep as is"* changes
 * nothing. Memory-only, on `state.different` like 7.5's, never the holding.
 * ⚠ THE ARTICLE IS THE BUILDER'S, beyond his wording: 145 catalogue rows
 * are said with "an" — $8 (76), 18d (27), 8s (14), 8d (12), £8 (6), $80
 * (5), 18s (3), $11, 11s — and "a $8 note" misreads them. The read value
 * is printed as the reader gave it ("Fifty Cents"), and the view escapes
 * both. */
const AN = /^(?:[$£]\s*)?(?:8|1[18](?![\d,])|1[18],\d{3}(?!\d))|^(?:eight|eleven|eighteen|eighty)/i
export const withArticle = (d) => { const s = String(d == null ? '' : d).trim(); return (AN.test(s) ? 'an ' : 'a ') + s }
export const RAW_DIFFERENT_LINE = (read, noteDenom) =>
  'This photo looks like ' + withArticle(read) + ' note, but this note is ' + withArticle(noteDenom) + '.'
export const DIFFERENT_SEE = 'See likely notes'

/* ══ HIS WALK OF STEP 6, 2026-10-04 — THE PHOTOS FIRST ON A CUSTOM NOTE ══
 *
 * *"when I tap Add from the vault, put the photo frames above the Note
 * details card, with a short line just below the frames, something like
 * "Take a photo to autofill the note details.""* — and his ruling 1 the
 * same day: *"Rule (a): every custom note gets the photos-first layout, the
 * line only while the front frame is empty. The reading card goes under the
 * photos: frames, the line, the reading card, then Note details."* The
 * words are the ones he offered, taken as written. A catalogue note
 * (Resources → Add, or a custom note once identified) keeps its layout and
 * never shows the line: *"the collector has already chosen the Fr number"*. */
export const PHOTO_FIRST_LINE = 'Take a photo to autofill the note details.'

/* ══ STEP 6 — THE READING CARD, "WHAT THE PHOTO SHOWS" — HIS RULING
 * 2026-09-26, THE "AI FILLS, COLLECTOR FINISHES" DESIGN ═══════════════
 *
 * *"After a reading, show the read details as editable fields (unread
 * ones as "not read — tap to add"); correcting or adding any detail
 * re-narrows the list live; the app never picks; a no-Fr answer ends the
 * lookup. Correct, not clear, is the invited action."*
 *
 * A RAW reading (the v3 prompt, `engine` carrying `:raw-v3`) has no Fr to
 * match. Its six fields go on `state.reading`, MEMORY-ONLY like the
 * lookup line and the 7.5 prompt — gone on Save, on leaving and on
 * reload — and the view narrows the catalogue from them on every edit.
 * ⚠ THE WORDING IS HIS, approved 2026-09-26 as rendered in the seven
 * mockups, and is final as written. `narrow-ui-check` asserts every
 * string from HERE and from the view both (the 7.5 lesson). */
export const READING_FIELDS = Object.freeze(['denomination', 'series', 'type', 'star_serial', 'state', 'district'])
/* ⚠ THE CARD'S ORDER IS THE MOCKUPS' — his catch, 2026-09-26: the seven
 * renders he approved show Denomination · Type · Series · Star note ·
 * State or colony · Federal Reserve Bank, and the first build followed
 * the prompt's list (Series before Type). The view renders in THIS order
 * and the gate asserts it. Same six fields, a permutation. */
export const CARD_ORDER = Object.freeze(['denomination', 'type', 'series', 'star_serial', 'state', 'district'])
/* ── STOP B, HIS RULINGS 2026-09-27: TWO DETAILS THE COLLECTOR SETS ────
 * *Charter period* (Large Size National Bank Notes) and *Variety* (Small
 * Size National Bank Notes) are the collector's — the AI never reads
 * them — so they are NOT in `READING_FIELDS`, which is the v3 prompt's
 * list and gated against the encoder file. The card shows its details in
 * `DETAIL_ORDER`, the approved third-round mockups' order with Variety
 * after Charter period; `note-narrow.js` `cardState` says which show. */
/* ── THE STOP B AMENDMENT, 2026-10-01: SEVEN MORE THE COLLECTOR ANSWERS —
 * his rulings F, G, I and #8 of 2026-09-28 with his answers of 2026-09-29.
 * Each is a QUESTION on the card over a field checkpoint 1 wrote to the
 * catalogue (`note-narrow.js` `QUESTIONS` carries the values and the
 * order; the strings are below): the Nationals' seal/back and back type
 * (#8), the Legal Tender serial colour (F), the Black Eagle's series-date
 * position (G), the Fractional issue, design and back colour (I). The
 * reader never reads them — *"the collector always answers these"*. */
export const QUESTION_FIELDS = Object.freeze(['seal', 'backType', 'serial', 'datePosition', 'issue', 'design', 'back'])
export const COLLECTOR_FIELDS = Object.freeze(['charter', 'variety', ...QUESTION_FIELDS])
/* The value of the Type select's LEAD option — the question, or a read
 * heading no option names — while the READ heading stands. Never a
 * section's name ("Silver Certificate" read must not collide with the
 * Large Size option), and never stored: the change handler ignores it,
 * so picking it leaves the read state as it was (his catch 2026-09-27).
 * ⚠ The amendment's question rows carry the same lead value for THEIR
 * question; on them, picking it again clears the answer (the question
 * comes back), since those fields are never read and have no "not read". */
export const TYPE_LEAD = '__read'
/* ⚠ A1, HIS RULING OF 2026-10-30: *"A(i) yes — confirming the AI's Other
 * makes it mine and it sticks; A(ii) A1; B: B1."* On an Other the AI filled,
 * the Type select's SELECTED option is this lead (the text `CARD.otherFilled`)
 * and plain Other is offered after the 22 — so picking Other is a value
 * change the browser reports (picking the already-selected Other fired no
 * `change`, his walk of `758af329`, step 6). Picking the lead again is a
 * no-op, like `TYPE_LEAD` (main.js). On the card and in Note details. */
export const OTHER_LEAD = '__other-read'
/* ~~['denomination', 'type', 'series', 'charter', 'variety', 'star_serial',
 * 'state', 'district']~~ — the amendment's rows follow the detail they
 * depend on: #8's two after Charter period, then Variety, then F, G and
 * I's, then Star, State and the Bank as approved. */
export const DETAIL_ORDER = Object.freeze(['denomination', 'type', 'series', 'charter', 'seal', 'backType', 'variety', 'serial', 'datePosition', 'issue', 'design', 'back', 'star_serial', 'state', 'district'])
export const isRawReading = (read) => /:raw-v\d+/.test(String((read && read.engine) || ''))
export const CARD = Object.freeze({
  title: 'What the photo shows',
  intro: "Read from your front photo. Fix anything that's off, or add what it couldn't read — the matches below narrow as you go.",
  labels: Object.freeze({ denomination: 'Denomination', type: 'Type', series: 'Series', charter: 'Charter period', variety: 'Variety', star_serial: 'Star note', state: 'State or colony', district: 'Federal Reserve Bank',
    /* ⚠ THE AMENDMENT'S ROW LABELS ARE THE BUILDER'S, 2026-10-01 — his
     * rulings gave the questions and the answers, not the labels; shown to
     * him in the renders. `seal` reads "Seal" under Third Charter and
     * "Back" under Second (`questionLabel`), because that question asks
     * about the seal on one and the back on the other. */
    seal: 'Seal', backType: 'Back type', serial: 'Serial numbers', datePosition: 'Series date', issue: 'Issue', design: 'Design', back: 'Back' }),
  sealLabelBack: 'Back',
  /* THE QUESTIONS — his wording, approved 2026-09-29 (*"Correct. Your
   * proposed wording."*) and his #8 of 2026-09-28 in his own words. #8's
   * four are the answers joined, which gives exactly his four sentences:
   * "Red Seal or Blue Seal?", "Date Back or Plain Back?", "Brown Back or
   * Green Back?", "Date Back or Value Back?". */
  question: Object.freeze({ serial: 'Red or blue serial numbers?', datePosition: 'Where is the series date?', issue: 'Which issue?', design: 'Spinner or Justice?', back: 'Red back or green back?' }),
  askChoice: (answers) => answers.join(' or ') + '?',
  /* THE ANSWERS as the card prints them — his approved wording where he
   * gave one (G's three, F's "Red" / "Blue", I's issue names, "Spinner" /
   * "Justice", "Red back" / "Green back"); #8's answers are his words and
   * the catalogue's own. A value not named here prints as itself. */
  answer: Object.freeze({
    datePosition: Object.freeze({ 'above Serial Number': 'Above the serial number', 'below Serial Number': 'Below the serial number', 'to right of Seal': 'To the right of the seal' }),
    back: Object.freeze({ Red: 'Red back', Green: 'Green back' }),
  }),
  questionText: (field, answers) => CARD.question[field] || CARD.askChoice(answers),
  answerText: (field, v) => (CARD.answer[field] && CARD.answer[field][v]) || v,
  questionLabel: (field, answers) => (field === 'seal' && answers.some((a) => /Back$/.test(a)) ? CARD.sealLabelBack : CARD.labels[field]),
  /* ⚠ STOP B's STRINGS — each as he approved it (BUILD_NOTES.md, RESUME
   * HERE): the third-round mockups *"approved as drawn"* (the size
   * question, *Large size* / *Small size*, *Charter period* and its three
   * values, *Add another detail*, the placeholder), his F line verbatim,
   * the Variety label and options (his answer 6), and the Treasury
   * question (*"approved as proposed"*). */
  addMore: 'Add another detail',
  noFrObsolete: "Obsolete notes have no Fr. number; it's saved as an Obsolete Note.",
  askSize: (name) => name + ' — large or small?',
  large: 'Large size',
  small: 'Small size',
  askIssue: 'Treasury Note — 1812–1861 or 1890–91?',
  issueOld: '1812–1861',
  issueNew: '1890–91',
  charterOption: (v) => v + ' Charter Period',
  varietyOption: Object.freeze({ 'Type 1': 'Type 1 — charter number printed twice', 'Type 2': 'Type 2 — charter number printed four times' }),
  statePlaceholder: 'e.g. Connecticut',
  notRead: 'not read — tap to add',
  /* the AI-filled Other's lead option (A1); the builder's wording, his to judge on screen */
  otherFilled: 'Other ✨ — filled from your photo',
  starYes: 'Yes',
  starNo: 'No',
  readMark: 'Read from your photo',
  typedMark: 'Your correction',
  tapYours: 'tap yours',
  matchesOne: 'One catalog note matches',
  matchesN: (n) => 'Could be one of these ' + n,
  tooMany: (n) => n + ' catalog notes match so far — add or fix a detail above to narrow it down.',
  nothing: 'No catalog note matches these details. One of them may be off — fix it above, or enter the Fr. number below.',
  noFr: 'Notes like this have no Fr. number in the catalog — keep it as a custom note.',
  pick: 'This is my note',
  /* ⚠ HIS RULING 2 OF 2026-10-06: on a CATALOGUE note (the card opened by
   * "See likely notes") the three lines written for a custom note get
   * catalogue versions — *"no wording about custom notes, saving, or an Fr.
   * field below; each ends with keeping this note as it is."* ⏸ THE WORDING
   * IS THE BUILDER'S PROPOSAL; he judges it in the render. */
  nothingKeep: 'No catalog note matches these details. One of them may be off — fix it above, or keep this note as it is.',
  noFrKeep: 'Notes like this have no Fr. number in the catalog, so none can be listed here — keep this note as it is.',
  noFrObsoleteKeep: 'Obsolete notes have no Fr. number, so none can be listed here — keep this note as it is.',
})

const readingKey = (holdingId) => String(holdingId)
const cleanValue = (field, v) => {
  const s = v == null ? '' : String(v).trim()
  if (field === 'star_serial') { const t = s.toLowerCase(); return t === 'yes' || t === 'no' ? t : null }
  return s === '' ? null : s.slice(0, 40)
}

/** Record a raw reading for one holding. ⚠ ON `state`, NEVER ON THE
 *  HOLDING. `read` marks the fields the reader gave a value; `unsure`
 *  on the star is unread (his wording: "not read — tap to add"). */
export function setReading(state, holdingId, { fields, engine, ms }) {
  if (!state.reading) state.reading = {}
  const src = fields && typeof fields === 'object' ? fields : {}
  /* `revealed`: "Add another detail" tapped. `noFr`: which note fields the
   * no-Fr rule filled and the collector has not touched since (suggest.js
   * `syncNoFrFill`) — on the card's entry because they live exactly as
   * long as the card does: gone on Save, on leaving, on reload. */
  const entry = { holdingId: String(holdingId), fields: {}, read: {}, typed: {}, engine: engine || null, ms: ms ?? null, revealed: false, noFr: {} }
  for (const f of READING_FIELDS) { const v = cleanValue(f, src[f]); entry.fields[f] = v; entry.read[f] = v != null; entry.typed[f] = false }
  for (const f of COLLECTOR_FIELDS) { entry.fields[f] = null; entry.read[f] = false; entry.typed[f] = false }
  state.reading[readingKey(holdingId)] = entry
  return entry
}

/** "Add another detail" — the rest of the details show (his point 2). */
export function revealDetails(state, holdingId) {
  const r = getReading(state, holdingId)
  if (!r) return null
  r.revealed = true
  return r
}

/** The collector corrects or adds one detail. An edit is theirs: the ✨
 *  goes and the ✎ comes, and the view re-narrows on the next render.
 *  ⚠ An unknown field is refused, not stored. */
export function setReadingField(state, holdingId, field, value) {
  const r = getReading(state, holdingId)
  if (!r || !(READING_FIELDS.includes(field) || COLLECTOR_FIELDS.includes(field))) return null
  r.fields[field] = cleanValue(field, value)
  r.typed[field] = true
  r.read[field] = false
  return r
}

export const getReading = (state, holdingId) => (state.reading || {})[readingKey(holdingId)] || null

export function clearReading(state, holdingId) {
  if (!state.reading) return false
  return delete state.reading[readingKey(holdingId)]
}

/** Record the prompt for one holding: the row the photo matched and the
 *  reading, so Update can take the label's grade without asking again.
 *  ⚠ 2026-10-06: or a RAW prompt (`raw: true`, no row — a raw reading
 *  names none), whose `open` says "See likely notes" was tapped. */
export function setDifferent(state, holdingId, entry) {
  if (!state.different) state.different = {}
  const e = {
    holdingId: String(holdingId), sid: entry.raw ? null : String(entry.sid), raw: !!entry.raw, open: false,
    /* his ruling of 2026-10-22: set while the collector was NOT on the holding
     * view → the collection card carries a small mark while this entry stands */
    mark: !!entry.mark,
    read: { ok: true, engine: entry.read && entry.read.engine || null, ms: entry.read ? entry.read.ms ?? null : null, fields: { ...(entry.read && entry.read.fields || {}) } },
  }
  state.different[lookupKey(holdingId)] = e
  return e
}

export const getDifferent = (state, holdingId) =>
  (state.different || {})[lookupKey(holdingId)] || null

export function clearDifferent(state, holdingId) {
  if (!state.different) return false
  return delete state.different[lookupKey(holdingId)]
}

/* ══ HIS RULING OF 2026-10-22 — A REPLACED FRONT PHOTO THE APP DID NOT CHECK ══
 *
 * The register item "a replaced front photo of a different note goes
 * unnoticed" (2026-10-19: a slab's front replaced with a raw $1,000 note,
 * and the app said nothing — no reading was made). The two prompts above
 * speak only when a reading CONTRADICTS the note. Three exits were silent:
 * the reading failed (`read.ok` false, any reason), a raw reading of the
 * SAME denomination (which can neither confirm nor contradict a catalogue
 * note — the raw prompt compares denomination alone), and a custom note
 * with a typed Fr (never compared). On a REPLACEMENT — the slot carried an
 * `r2:` ref before this upload (`suggest.js` tests it) — each now draws ONE
 * plain line under the hero, his exact text, with Dismiss. A first photo
 * draws nothing. The line claims only what the app knows: that it did not
 * check. Nothing on the holding changes; no call is made. Memory-only, on
 * `state.unchecked`, like the prompts; `clearPendingFor` forgets it with
 * them (leaving the note or Save), and a new landing on the note supersedes
 * it. */
export const UNCHECKED_LINE = "This photo wasn't checked against the note's details."
export const UNCHECKED_DISMISS = 'Dismiss'

export function setUnchecked(state, holdingId, why) {
  if (!state.unchecked) state.unchecked = {}
  const e = { holdingId: String(holdingId), why: String(why) }
  state.unchecked[lookupKey(holdingId)] = e
  return e
}

export const getUnchecked = (state, holdingId) =>
  (state.unchecked || {})[lookupKey(holdingId)] || null

export function clearUnchecked(state, holdingId) {
  if (!state.unchecked) return false
  return delete state.unchecked[lookupKey(holdingId)]
}

/* ══ THE LOOK — step 4.2, his ruling: CANDIDATE A ═══════════════════
 *
 * **2px dashed `--forest`, the value's ink unchanged at full strength,
 * no fill.** ⚠⚠ THE INK IS THE POINT: dimming the text is what makes a
 * field read as DISABLED, and a suggestion is not disabled — it is
 * active and provisional. So the signal is carried entirely by a
 * BOUNDARY, and the boundary is dashed because a dash is the one
 * convention that means *not committed* without claiming a category.
 *
 * ⚠ A FILL COULD NOT HAVE CARRIED IT. Measured 2026-09-20: every tint
 * in the palette is ~1.1:1 against white — `--year-bg` 1.13, `--mint`
 * 1.11, `--grade-bg` 1.11 — the same invisibility as the photo editor's
 * `.rot` at 1.12:1 that he reclassified as an accessibility defect. Only
 * a border or the ink can reach 3:1, and the ink is spoken for.
 *
 * ⚠ `--gold`, the app's action colour, is 2.44:1 on white and could not
 * have been the boundary either.
 */
export const SUGGESTED_CLASS = 'sugg'
export const SUGGESTED_VALUE_CLASS = 'sugg-v'

/** The class a FIELD carries while it holds an unconfirmed suggestion. */
export const pendingClass = (state, holdingId, field) =>
  (getPending(state, holdingId, field) ? SUGGESTED_CLASS : '')

/** The class the VALUE carries. ⚠ Separate from the field's so the
 *  boundary and the text can never be styled by one rule that a later
 *  edit dims by accident. */
export const pendingValueClass = (state, holdingId, field) =>
  (getPending(state, holdingId, field) ? SUGGESTED_VALUE_CLASS : '')
